import React, { useState, Suspense } from 'react';
import PropTypes from 'prop-types';
import { httpAssignCompanyToUser } from '../../../Hooks/admin/admin.put.hooks';
import { httpRemoveUserFromCompany } from '../../../Hooks/company/company.put.hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CompaniesDataProvider from '../../../Utils/Companies/CompaniesDataProvider';
const PrimaryButtonComponent = React.lazy(
  () =>
    import('../../../Components/Buttons/PrimaryButton/PrimaryButton.component')
);
import SecondaryButton from '../../../Components/Buttons/SecondaryButton/SecondaryButton.component';
import { FormBackground } from '../../../AdminRoutes/CreateRoutes/CreateForm.styles';

const AssignUserToCompany = ({ userId, userCompany }) => {
  const [selectedCompany, setSelectedCompany] = useState('');
  const [type, setType] = useState('Manager');
  const [removedStatus, setRemovedStatus] = useState('');
  const [assignedStatus, setAssignedStatus] = useState('');

  const queryClient = useQueryClient();

  const assignMutation = useMutation({
    mutationFn: ({ userId, selectedCompany, type }) =>
      httpAssignCompanyToUser(userId, selectedCompany, type),
    onSuccess: async (data) => {
      let message = await data.json();
      queryClient.invalidateQueries(['users', 'user']);
      setAssignedStatus(message.msg || 'User has been assigned');
    },
    onError: async (error) => {
      let err = await error.json();
      console.error('Error purchasing course:', error);
      setAssignedStatus(err || 'Failed to assign user to a company');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId) => httpRemoveUserFromCompany(userId),
    onSuccess: async (data) => {
      let message = await data.json();
      queryClient.invalidateQueries(['users', 'user']);
      setRemovedStatus(message.msg || 'User has been removed from the company');
    },
    onError: async (error) => {
      let err = await error.json();
      console.error('Error removing user from company:', error);
      setRemovedStatus(err || 'Failed to remove user from the company');
    },
  });

  const assignUser = async (e) => {
    e.preventDefault();
    setAssignedStatus('Adding user to the company');
    assignMutation.mutate({ userId, selectedCompany, type });
  };

  const removeUserFromCompany = async (e) => {
    e.preventDefault();
    setRemovedStatus('Removing user from company...');
    removeMutation.mutate(userId);
  };

  return (
    <CompaniesDataProvider>
      {(companies) => (
        <>
          <form onSubmit={assignUser}>
            <FormBackground>
              <h4>
                Company{' '}
                <span style={{ letterSpacing: '0' }}>
                  Assign a user to a company
                </span>
              </h4>
              {assignedStatus && <p>{assignedStatus}</p>}
              <div className="formRow">
                <div className="inputGroup">
                  <select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                  >
                    <option value="">Select a company</option>
                    {companies?.map((company) => (
                      <option key={company._id} value={company._id}>
                        {company.companyName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="inputGroup">
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    <option value="Manager">Manager</option>
                    <option value="Employee">Employee</option>
                  </select>
                </div>
                <div className="buttonGroup">
                  <Suspense fallback={<p>Loading button...</p>}>
                    <PrimaryButtonComponent
                      span="assign"
                      functionName={assignUser}
                    />
                  </Suspense>
                </div>
              </div>
            </FormBackground>
          </form>
          <form className="m1T" onSubmit={removeUserFromCompany}>
            <FormBackground>
              <h4>
                Remove User from current company{' '}
                {userCompany && <span>({userCompany})</span>}
              </h4>
              {removedStatus && <p>{removedStatus}</p>}
              <div className="buttonGroup">
                <SecondaryButton
                  span="Remove"
                  functionName={removeUserFromCompany}
                />
              </div>
            </FormBackground>
          </form>
        </>
      )}
    </CompaniesDataProvider>
  );
};

AssignUserToCompany.propTypes = {
  userId: PropTypes.string,
  userCompany: PropTypes.string,
};

export default AssignUserToCompany;
